/*
  * Copyright Amazon.com, Inc. and its affiliates. All Rights Reserved.
  * SPDX-License-Identifier: MIT
  *
  * Licensed under the MIT License. See the LICENSE accompanying this file
  * for the specific language governing permissions and limitations under
  * the License.
  */

import React from 'react';
import { connect } from 'react-redux';

import { Auth } from 'aws-amplify';
import { I18n } from '@aws-amplify/core';

import Config from "Config";

import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardActions from '@material-ui/core/CardActions';
import CardContent from '@material-ui/core/CardContent';
import FormControl from '@material-ui/core/FormControl';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import InputAdornment from '@material-ui/core/InputAdornment';
import { MailOutline, Smartphone } from '@material-ui/icons';

import { Branding } from '../../branding';
import LogOutButton from '../../components/LogoutButton/LogoutButton';
import VerifyAttributeDialog from '../../components/VerifyAttributeDialog/VerifyAttributeDialog';
import ChangePasswordDialog from '../../components/ChangePasswordDialog/ChangePasswordDialog';
import AppSnackbar from '../../components/Snackbar/Snackbar';
import useWindowDimensions from '../../components/ViewPort/useWindowDimensions';
import { setUser, setUserEmail, setUserPhonenumber } from '../../redux/actions';
import { isJapanesePhoneNumber, formatPhoneNumberForJapan, displayPhoneNumber } from '../../utils/phoneNumber';
import { API } from 'aws-amplify';

const useStyles = makeStyles((theme) => ({
	root: {

	},
	header: {
		backgroundColor: Branding.primary,
		color: Branding.white,
		height: 50,
		textAlign: 'center',
	},
	title: {
		fontSize: 14,
	},
	cardContent: {
		textAlign: 'left',
		paddingBottom: 0,
	},
	cardActions: {
		justifyContent: 'center',
	},
	boxInputField: {
		margin: theme.spacing(2),
	},
	textFieldIcon: {
		color: Branding.secondary,
	},
	formControl: {
		width: "-webkit-fill-available"
	},
	input: {

	},
	buttonChange: {
		marginTop: theme.spacing(1),
		marginLeft: theme.spacing(1),
	},
	buttonCancel: {
		marginTop: theme.spacing(1),
		marginLeft: theme.spacing(1),
		color: Branding.negative,
		'&:hover': {
			color: Branding.negative,
			opacity: Branding.opacityHover,
		},
	},
	buttonSave: {
		marginTop: theme.spacing(1),
		marginLeft: theme.spacing(1),
		color: Branding.positive,
		'&:hover': {
			color: Branding.positive,
			opacity: Branding.opacityHover,
		},
	},
	buttonPasswordChange: {
		margin: theme.spacing(1),
		width: '-webkit-fill-available',
	}
}));

const mapStateToProps = (state) => {
	const rawPhoneNumber = state.user.attributes.phone_number || '';
	return {
		lang: state.app.lang,

		username: state.user.username || '',
		email: state.user.attributes.email || '',
		email_verified: state.user.attributes.email_verified || false,
		phone_number: rawPhoneNumber,
		// 表示用の電話番号（E.164形式から国内形式に変換）
		phone_number_display: displayPhoneNumber(rawPhoneNumber),
		phone_number_verified: state.user.attributes.phone_number_verified || false,
	}
}

const TabSignInData = (props) => {
	const classes = useStyles();
	const { width } = useWindowDimensions();
	const mode = props.mode || 'all';
	const [editEmail, setEditEmail] = React.useState(mode === 'email');
	const [editPhoneNumber, setEditPhoneNumber] = React.useState(mode === 'phone');
	const [editingPhoneValue, setEditingPhoneValue] = React.useState('');
	const [passwordChange, setPasswordChange] = React.useState(mode === 'password');
	const [verifyAttribute, setVerifyAttribute] = React.useState({
		type: '',
		open: false
	});
	const [snackBarOps, setSnackBarOps] = React.useState({
		type: 'info',
		open: false,
		vertical: 'top',
		horizontal: 'center',
		autoHide: 0,
		message: ''
	});

	const isExternalClient = !!props.redirectUri;

	const redirectToClient = () => {
		if (!props.redirectUri) return;
		let url = props.redirectUri;
		if (props.clientState) {
			url += (url.includes('?') ? '&' : '?') + 'state=' + encodeURIComponent(props.clientState);
		}
		window.location.href = url;
	};

	// To initiate the process of verifying the attribute like 'phone_number' or 'email'
	const verifyCurrentUserAttribute = (attr) => {
		Auth.verifyCurrentUserAttribute(attr)
			.then(() => {
				setVerifyAttribute({ type: attr, open: true });
			}).catch((err) => {
				console.log(err);
				props.reloadUserData();

				setSnackBarOps({
					type: 'error',
					open: true,
					vertical: 'top',
					horizontal: 'center',
					autoHide: 3000,
					message: I18n.get('TAB_SIGNIN_DATA_MESSAGE_EROR')
				})
			});
	}

	const callHerokuSync = async (accountKey, attr, jsonAttributes) => {
		const syncBody = { accountKey };
		if (attr === 'email') syncBody.email = jsonAttributes.email;
		if (attr === 'phone_number') syncBody.tel = jsonAttributes.phone_number;
		await API.post('amplifyIdentityBrokerApi', '/herokuSync', { body: syncBody });
	};

	const showError = (messageKey) => {
		setSnackBarOps({
			type: 'error', open: true, vertical: 'top', horizontal: 'center',
			autoHide: 5000, message: I18n.get(messageKey)
		});
	};

	const updateUserAttributes = async (attributes, attr = null) => {
		const jsonAttributes = JSON.parse(attributes);
		const isHerokuTarget = (attr === 'email' || attr === 'phone_number');
		const isHerokuEnabled = Config.isHerokuEnabled;

		try {
			const user = await Auth.currentAuthenticatedUser();

			if (isHerokuTarget && isHerokuEnabled) {
				const accountKey = user.username;

				const oldValues = {};
				if (attr === 'email') oldValues.email = user.attributes.email;
				if (attr === 'phone_number') oldValues.phone_number = user.attributes.phone_number;

				try {
					await callHerokuSync(accountKey, attr, jsonAttributes);
				} catch (herokuErr) {
					console.error('HerokuSync failed:', herokuErr);
					showError('TAB_SIGNIN_DATA_MESSAGE_HEROKU_SYNC_FAILED');
					return;
				}

				try {
					await Auth.updateUserAttributes(user, jsonAttributes);
				} catch (cognitoErr) {
					console.error('Cognito update failed, rolling back Heroku:', cognitoErr);
					try {
						await callHerokuSync(accountKey, attr, oldValues);
					} catch (rollbackErr) {
						console.error('Heroku rollback failed:', rollbackErr);
					}
					showError('TAB_SIGNIN_DATA_MESSAGE_EROR');
					return;
				}

				setVerifyAttribute({ type: attr, open: true });
				setSnackBarOps({
					type: 'info', open: true, vertical: 'top', horizontal: 'center',
					autoHide: 3000, message: I18n.get('TAB_SIGNIN_DATA_MESSAGE_UPDATE_ATTRIBUTE_VERIFYCATION_REQUEST')
				});
			} else if (isHerokuTarget) {
				await Auth.updateUserAttributes(user, jsonAttributes);
				setVerifyAttribute({ type: attr, open: true });
				setSnackBarOps({
					type: 'info', open: true, vertical: 'top', horizontal: 'center',
					autoHide: 3000, message: I18n.get('TAB_SIGNIN_DATA_MESSAGE_UPDATE_ATTRIBUTE_VERIFYCATION_REQUEST')
				});
			} else {
				await Auth.updateUserAttributes(user, jsonAttributes);
				setSnackBarOps({
					type: 'success', open: true, vertical: 'top', horizontal: 'center',
					autoHide: 3000, message: I18n.get('TAB_SIGNIN_DATA_MESSAGE_UPDATE_ATTRIBUTE_SUCCESS')
				});
			}

			props.reloadUserData();
		} catch (err) {
			console.error('updateUserAttributes error:', err);
			showError('TAB_SIGNIN_DATA_MESSAGE_EROR');
		}
	};

	const handleEmailChange = (value) => {
		//if (!value) return;

		setEditEmail(true);
		props.setUserEmail(value);
	};

	const handlePhoneNumberChange = (value) => {
		//if (!value) return;

		setEditPhoneNumber(true);
		setEditingPhoneValue(value);
	};

	const handleAttributeChange = (attr) => {
		switch (attr) {
			case 'email':
				setEditEmail(true);
				break;
			case 'phone_number':
				setEditPhoneNumber(true);
				break;
			case 'password':
				setPasswordChange(true);
				break;
			default:
				break;
		}
	};

	const handleAttributeCancel = (attr) => {
		switch (attr) {
			case 'email':
				setEditEmail(false);
				break;
			case 'phone_number':
				setEditPhoneNumber(false);
				setEditingPhoneValue('');
				break;
			default:
				break;
		}

		props.reloadUserData();
	};

	const handleAttributeSave = (attr) => {
		switch (attr) {
			case 'email':
				if (validateEmailAddress(props.email)) {
					updateUserAttributes(`{"email": "${props.email}"}`, 'email');
					setEditEmail(false);
				} else {
					setSnackBarOps({
						type: 'error',
						open: true,
						vertical: 'top',
						horizontal: 'center',
						autoHide: 3000,
						message: I18n.get('TAB_SIGNIN_DATA_MESSAGE_EMAIL_VALIDATION_ERROR')
					});
				}
				break;
			case 'phone_number':
				const formattedPhone = formatPhoneNumberForSave(editingPhoneValue);
				if (validatePhoneNumber(formattedPhone)) {
					updateUserAttributes(`{"phone_number": "${formattedPhone}"}`, 'phone_number');
					setEditPhoneNumber(false);
					setEditingPhoneValue('');
				} else {
					setSnackBarOps({
						type: 'error',
						open: true,
						vertical: 'top',
						horizontal: 'center',
						autoHide: 3000,
						message: I18n.get('TAB_SIGNIN_DATA_MESSAGE_PHONE_NUMBER_VALIDATION_ERROR')
					});
				}
				break;
			default:
				break;
		}
	};

	const handleCloseVerifyDialog = async (successful = false, attrType = '') => {
		if ((attrType === 'email' || attrType === 'phone_number') && successful === true) {
			if (isExternalClient) {
				redirectToClient();
				return;
			}
			try {
				await Auth.signOut();
				window.location.href = '/';
				return;
			} catch (err) {
				console.log('Error signing out:', err);
			}
		}

		if (successful === true) {
			setSnackBarOps({
				type: 'success',
				open: true,
				vertical: 'top',
				horizontal: 'center',
				autoHide: 3000,
				message: I18n.get('TAB_SIGNIN_DATA_MESSAGE_VERIFY_ATTRIBUTE_MESSAGE_SUCCESS')
			});
		}

		setVerifyAttribute({ type: '', open: false })
	}

	const handleClosePasswordDialog = (successful = false) => {
		if (successful === true) {
			if (isExternalClient) {
				redirectToClient();
				return;
			}
			setSnackBarOps({
				type: 'success',
				open: true,
				vertical: 'top',
				horizontal: 'center',
				autoHide: 3000,
				message: I18n.get('TAB_SIGNIN_DATA_MESSAGE_PASSWORD_CHANGE_SUCCESS')
			});
		};

		setPasswordChange(false);
	}

	const validateEmailAddress = (emailAddress = "") => {
		var reg = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,24}))$/;

		return emailAddress === "" ? true : reg.test(emailAddress) ? true : false;
	}

	const validatePhoneNumber = (phonenumber = "") => {
		var reg = /^\+[0-9]{3,}$/;

		return phonenumber === "" ? true : reg.test(phonenumber) ? true : false;
	}

	// 電話番号の保存用フォーマット（共通ユーティリティを使用）
	const formatPhoneNumberForSave = (phoneNumber) => {
		if (!phoneNumber) return '';
		if (isJapanesePhoneNumber(phoneNumber)) {
			return formatPhoneNumberForJapan(phoneNumber);
		}
		return phoneNumber;
	};

	const showEmail = (mode === 'all' || mode === 'email');
	const showPhone = (mode === 'all' || mode === 'phone');
	const showPassword = (mode === 'all' || mode === 'password');
	const showLogout = (mode === 'all');

	return (
		<div>
			{snackBarOps.open && (
				<AppSnackbar ops={snackBarOps} />
			)}

			{verifyAttribute.open && (
				<VerifyAttributeDialog
					attrType={verifyAttribute.type}
					open={verifyAttribute.open}
					close={(successful) => handleCloseVerifyDialog(successful, verifyAttribute.type)}
					reloadUserData={props.reloadUserData}
				/>
			)}

			{passwordChange && (
				<ChangePasswordDialog
					open={passwordChange}
					close={(successful) => handleClosePasswordDialog(successful)}
					redirectUri={props.redirectUri}
				/>
			)}

			<Card className={classes.root} variant="outlined">
				<CardHeader
					className={classes.header}
					title={I18n.get('TAB_SIGNIN_DATA_LABEL')}
					disableTypography={width >= 375 ? false : true}
					titleTypographyProps={{}}
				/>
				<CardContent className={classes.cardContent}>
					{showEmail && (
					< Box className={classes.boxInputField} >
						<FormControl className={classes.formControl}>
							<InputLabel htmlFor="inputEmail">
								{I18n.get('TAB_SIGNIN_DATA_EMAIL_INPUT_LABEL')}
							</InputLabel>
							<Input
								value={props.email}
								id="inputEmail"
								onChange={(event) => handleEmailChange(event.target.value)}
								startAdornment={
									<InputAdornment position="start">
										<MailOutline className={classes.textFieldIcon} />
									</InputAdornment>
								}
								className={classes.input}
								inputProps={{ style: { left: 0 } }}
							/>
						</FormControl>
						{editEmail && (
							<div>
								<Button
									variant="outlined"
									onClick={() => handleAttributeSave('email')}
									className={classes.buttonSave}
								>
									{I18n.get('TAB_SIGNIN_DATA_SAVE_BUTTON_LABEL')}
								</Button>
								<Button
									variant="outlined"
									onClick={() => handleAttributeCancel('email')}
									className={classes.buttonCancel}
								>
									{I18n.get('TAB_SIGNIN_DATA_CANCEL_BUTTON_LABEL')}
								</Button>
							</div>
						)}
					</Box >
					)}

					{showPhone && (
					< Box className={classes.boxInputField} >
						<FormControl className={classes.formControl}>
							<InputLabel htmlFor="inputPhoneNumber">
								{I18n.get('TAB_SIGNIN_DATA_PHONENUMBER_INPUT_LABEL')}
							</InputLabel>
							<Input
								value={editPhoneNumber ? editingPhoneValue : props.phone_number_display}
								onChange={(event) => handlePhoneNumberChange(event.target.value)}
								id="inputPhoneNumber"
								startAdornment={
									<InputAdornment position="start">
										<Smartphone className={classes.textFieldIcon} />
									</InputAdornment>
								}
								className={classes.input}
								inputProps={{ style: { left: 0 } }}
							/>
						</FormControl>
						{editPhoneNumber && (
							<div>
								<Button
									variant="outlined"
									onClick={() => handleAttributeSave('phone_number')}
									className={classes.buttonSave}
								>
									{I18n.get('TAB_SIGNIN_DATA_SAVE_BUTTON_LABEL')}
								</Button>
								<Button
									variant="outlined"
									onClick={() => handleAttributeCancel('phone_number')}
									className={classes.buttonCancel}
								>
									{I18n.get('TAB_SIGNIN_DATA_CANCEL_BUTTON_LABEL')}
								</Button>
							</div>
						)}
					</Box >
					)}

					{showPassword && (
					< Box className={classes.boxInputField} >
						<Button
							variant="contained"
							color="secondary"
							onClick={() => handleAttributeChange('password')}
							className={classes.buttonPasswordChange}
						>
							{I18n.get('TAB_SIGNIN_DATA_CHANGE_PASSWORD_LABEL')}
						</Button>
					</Box >
					)}

				</CardContent >
				{showLogout && (
				<CardActions className={classes.cardActions}>
					<LogOutButton />
				</CardActions>
				)}
			</Card >
		</div>
	)
}

export default connect(mapStateToProps, { setUser, setUserEmail, setUserPhonenumber })(TabSignInData)
